import React, { forwardRef } from 'react';
import { IconButton } from 'components/buttons';
import { useIconButtonHoverAnimation } from 'components/buttons/hooks/useIconButtonHoverAnimation';
import { Icon } from 'components/icons/Icon';
import classNames from 'classnames';

interface IHelpCenterButtonProps extends React.HTMLProps<{}> {
  count?: number;
  isActive?: boolean;
  href?: string;
}

/**
 * Help Center Button, icon and optional counter.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export const HelpCenterButton = forwardRef(
  (
    { className, count, href, isActive, onClick, title, style }: IHelpCenterButtonProps,
    ref: React.Ref<HTMLElement>
  ) => {
    const { iconStyle, hovered, ...animationProps } = useIconButtonHoverAnimation({
      isActive,
      sizes: {
        starting: '1rem',
        ending: '1.4rem',
      },
    });

    return href ? ( // TODO perhaps IconLink is a worthwhile abstraction?
      <a
        className={classNames('dpc-icon-button', className)}
        title={title}
        href={href}
        target="_blank"
        rel="noreferrer"
        ref={ref as any}
        style={style}
        {...animationProps}
      >
        <Icon className={'dp-help-center__icon'} animationStyle={iconStyle} />
      </a>
    ) : (
      <IconButton
        ref={ref as any}
        className={className}
        title={title}
        onClick={onClick}
        iconClassName="dp-help-center__icon"
        style={style}
        iconStyle={iconStyle}
        {...animationProps}
      >
        {!!count && <span className="dp-help-center__counter">{getDisplayCount(count)}</span>}
      </IconButton>
      // TODO spacing is off now
    );
  }
);

function getDisplayCount(count: number) {
  return count > 9000
    ? '9k+' // cap it at 9k, because y2k
    : count > 999
    ? Math.floor(count / 1000) + 'k' + (count % 1000 !== 0 ? '+' : '')
    : count.toString();
}
